import { useEffect, useRef, useState } from 'react';
// Namespace import is needed because Vite's CJS interop doesn't always
// unwrap react-use-websocket@4's default export when importing it as
// `import useWebSocket, { ReadyState } from 'react-use-websocket'`.
import * as RUWS from 'react-use-websocket';
const useWebSocket = RUWS.default;
const ReadyState = RUWS.ReadyState;

import { env } from '@/env';

interface UseChatOptions {
  room: string;
  /** Centrifugo JWT — fetched from `/users/:id/ws-token` via `useWsTokenQuery`. */
  token: string | null;
  /** Optional callback fired for every incoming Centrifugo "publication". */
  onPublish?: (data: unknown) => void;
}

/**
 * Subscribes to a Centrifugo channel over the QLASH WebSocket gateway.
 *
 * Wire format mirrors mobile-app/src/hooks/useChat.tsx (manual handshake,
 * no `centrifuge-js` SDK):
 *   1. handshake → `{ id: 1, params: { token, name: "js" } }`
 *   2. subscribe → `{ id: 2, method: 1, params: { channel: room } }`
 *   3. publications arrive as `{ result: { channel, data: { data: <msg> }}}`
 *
 * The websocket is paused while the Telegram viewport is collapsed or the
 * browser tab is hidden — saves battery and avoids stale-message storms.
 */
export function useChat({ room, token, onPublish }: UseChatOptions) {
  const [active, setActive] = useState(true);
  const lastIdRef = useRef(2);

  useEffect(() => {
    const onVisibility = () => setActive(!document.hidden);
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, []);

  const shouldConnect = active && !!token && !!room;

  const { sendJsonMessage, lastJsonMessage, readyState } = useWebSocket(
    env.wsUrl,
    {
      onOpen: () => {
        if (!token) return;
        sendJsonMessage({ id: 1, params: { token, name: 'js' } });
        sendJsonMessage({ id: 2, method: 1, params: { channel: room } });
        lastIdRef.current = 2;
      },
      shouldReconnect: () => true,
      reconnectInterval: 5000,
    },
    shouldConnect,
  );

  useEffect(() => {
    if (!lastJsonMessage || !onPublish) return;
    const payload = lastJsonMessage as {
      result?: { data?: { data?: unknown }; channel?: string };
    };
    const data = payload.result?.data?.data;
    if (data !== undefined) onPublish(data);
  }, [lastJsonMessage, onPublish]);

  return {
    isConnected: readyState === ReadyState.OPEN,
    sendJsonMessage,
  };
}
